// Console login — Amazon Cognito Hosted UI, authorization-code + PKCE (D11-c,
// 2026-09-09). No library: the flow is three URLs and a fetch.
//
// Why this exists: the three console APIs (console-data, agent-copilot,
// agent-wrapup) are now behind an API Gateway JWT authorizer. The ID token
// from this login is the credential on every call (see lib/consoleApi.js),
// replacing the shared secret that used to ship in this bundle.
//
// This is a SECOND login, separate from Amazon Connect's. One-login via SAML
// is the Andrew-gated migration in docs/ops/connect-saml-migration-
// assessment-2026-09-09.md. Until then agents sign in here first, then to
// Connect from the console's own Log in button.
//
// Config comes from the build (infrastructure/agents-console/deploy.sh):
//   VITE_COGNITO_DOMAIN     e.g. https://knktr-agents.auth.ap-southeast-2.amazoncognito.com
//   VITE_COGNITO_CLIENT_ID  public SPA client id (no secret — PKCE instead)
// Redirect URI is always this page's origin + '/', which must be on the app
// client's callback list.

import { isDefinitiveAuthFailure, singleFlight } from '../lib/authRetry';   // D40

const DOMAIN = (import.meta.env.VITE_COGNITO_DOMAIN || '').replace(/\/+$/, '');
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const STORE_KEY = 'knktr.console.auth';
const PKCE_KEY = 'knktr.console.pkce';
const SCOPES = 'openid email profile';

export const isConfigured = () => Boolean(DOMAIN && CLIENT_ID);
const redirectUri = () => `${window.location.origin}/`;

// Row 234 (Storm 1 Nov): the Hosted UI always returns to the bare origin (the
// one registered callback URL), which dropped everything after the "?":
// ?ui=v4, ?demo=1&screen=acw, ?theme=light. login() now remembers the address
// it started from and completeLoginIfRedirected() returns there. Same-origin
// paths only: a value that is not a single-slash path is ignored.
export function safeReturnTo(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  return value;
}

function b64url(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function randomString(n = 64) {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return b64url(a).slice(0, n);
}
async function sha256(str) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
}
function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return null; }
}

function load() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch { return null; }
}
function save(session) {
  try {
    if (session) localStorage.setItem(STORE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORE_KEY);
  } catch { /* storage unavailable — session lives for this page only */ }
}

/** Kick off the Hosted UI login. Never returns (navigates away). */
export async function login() {
  const verifier = randomString(96);
  const challenge = b64url(await sha256(verifier));
  const state = randomString(24);
  const returnTo = safeReturnTo(window.location.pathname + window.location.search + window.location.hash);
  try { sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, returnTo })); } catch { /* ignore */ }
  const q = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    scope: SCOPES,
    redirect_uri: redirectUri(),
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${DOMAIN}/oauth2/authorize?${q}`);
}

/** Clear the local session and end the Hosted UI session too. */
export function logout() {
  save(null);
  const q = new URLSearchParams({ client_id: CLIENT_ID, logout_uri: redirectUri() });
  window.location.assign(`${DOMAIN}/logout?${q}`);
}

async function tokenRequest(params) {
  const res = await fetch(`${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: CLIENT_ID, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `token endpoint HTTP ${res.status}`);
  return data;
}

function sessionFrom(tokens, previousRefresh) {
  const claims = decodeJwt(tokens.id_token) || {};
  let groups = claims['cognito:groups'];
  if (!Array.isArray(groups)) groups = [];
  return {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token || previousRefresh || null,
    expiresAt: (claims.exp ? claims.exp * 1000 : Date.now() + (tokens.expires_in || 3600) * 1000),
    user: claims.email || claims['cognito:username'] || null,
    tenants: groups.filter(g => g.startsWith('tenant:')).map(g => g.slice(7)),
  };
}

/**
 * Finish a redirect back from the Hosted UI (?code=…&state=…). Returns the
 * session, or null if this page load was not a redirect. Strips the query.
 */
export async function completeLoginIfRedirected() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  if (!code) return null;
  let pkce = null;
  try { pkce = JSON.parse(sessionStorage.getItem(PKCE_KEY) || 'null'); } catch { pkce = null; }
  try { sessionStorage.removeItem(PKCE_KEY); } catch { /* ignore */ }
  // Always clean the URL, even on failure — a stale code must not be retried.
  url.searchParams.delete('code'); url.searchParams.delete('state');
  window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  if (!pkce || pkce.state !== state) throw new Error('Login state mismatch — please sign in again.');
  const tokens = await tokenRequest({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    code_verifier: pkce.verifier,
  });
  const session = sessionFrom(tokens);
  save(session);
  // Back to the address the sign-in started from, with its query (see
  // safeReturnTo). A full load, so flags read at start-up (?demo, ?screen,
  // ?ui) apply; the session is already saved, so it does not sign in again.
  const back = safeReturnTo(pkce.returnTo);
  const here = url.pathname + (url.search || '') + url.hash;
  if (back && back !== here) {
    window.location.replace(back);
  }
  return session;
}

/** Current session if its ID token is still valid (with 60 s headroom). */
export function currentSession() {
  const s = load();
  if (!s || !s.idToken) return null;
  if (Date.now() > (s.expiresAt || 0) - 60_000) return null;
  return s;
}

/** Refresh with the stored refresh token; null if that is not possible.
 *
 * D40 (2026-09-16): single-flight, and only a DEFINITIVE rejection ends the
 * session. Before this, every caller refreshed independently (the ID token
 * lasts 60 min and at least six callers poll or fire on their own schedule),
 * and ANY failure called save(null) — so one transient error or one loser in
 * a rotated-token race wiped a good session and the next API call went out
 * with no Authorization header, i.e. a 401 at the gateway. A transient
 * failure now leaves the stored session alone so the next call can retry.
 * See lib/authRetry.js. */
export const refreshSession = singleFlight(async () => {
  const s = load();
  if (!s?.refreshToken) return null;
  try {
    const tokens = await tokenRequest({ grant_type: 'refresh_token', refresh_token: s.refreshToken });
    const next = sessionFrom(tokens, s.refreshToken);
    save(next);
    return next;
  } catch (err) {
    if (isDefinitiveAuthFailure(err)) {
      console.warn('Console session refresh rejected — signing out:', err?.message || err);
      save(null);                       // the refresh token itself is finished; a re-login is required
      return null;
    }
    // Transient: network, 5xx, throttling, or a lost rotation race. Keep the
    // stored session — this call returns null (so the caller sends no token
    // and may see a 401), but the NEXT call retries instead of forcing a
    // full re-login.
    console.warn('Console session refresh failed, keeping the session to retry:', err?.message || err);
    return null;
  }
});

/** ID token for API calls — refreshes first if it is about to expire. */
export async function getIdToken() {
  const s = currentSession() || (await refreshSession());
  return s?.idToken || null;
}

export function readSession() { return load(); }
